/**
 * This component will represent a OneUp module which can be added to the marketing pages through amplience schema configuration
 *
 * @module views/components/OneUp
 * @memberof -Common
 */
import './OneUp.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Spacer from '@ulta/core/components/Spacer/Spacer';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import Tag from '@ulta/modules/Tag/Tag';

/**
 * Represents a OneUP component
 *
 * @method
 * @param {OneUpProps} props - React properties passed from composition
 * @returns OneUp
 */
export const OneUp = function( props ){
  const content = props.article;
  return (
    <div className='OneUp'>
      <div className={ classNames( 'OneUp__wrapper', {
        'OneUp__wrapper--offset':props.imageDisplayMode === 'offset',
        'OneUp__wrapper--50-50': props.imageDisplayMode === '50-50',
        'OneUp__wrapper--mirrored': props.imageDisplayMode === 'mirrored',
        'OneUp__wrapper--offsetMirror': props.imageDisplayMode === 'offsetMirror'
      }
      ) }
      >
        {
          props.title.text &&
          <div className='OneUp__heading'>
            <div className='OneUp__headingTitle'>
              <Text
                htmlTag={ props.title.htmlTag }
                textStyle={ props.title.textStyle }
                color={ props.title.color }
                textAlign={ props.title.textAlign || 'center' }
              >
                { props.title.text }
              </Text>
              { props.title.spacerValue &&
                <Spacer value={ props.title.spacerValue } />
              }
            </div>
            {
              props.subtitle &&
              <div className='OneUp__headingSubtitle'>
                <Text
                  htmlTag='p'
                  textStyle='subtitle-1'
                  textAlign='center'
                >
                  { props.subtitle }
                </Text>
              </div>
            }
          </div>
        }

        <div className='OneUp__contentImage'>
          {
            content.image &&
            <UltaAsset
              { ...spreadProps( { ...content.image } ) }
              shouldUseIntersectionObserver={ false }
            />
          }
        </div>

        <div className={
          classNames( 'OneUp__contentDetails', {
            ...props.backgroundColor ? { ['OneUp__contentDetails--' + props.backgroundColor]: true } : {}
          } )
        }
        >
          {
            content.tag &&
            <div className='OneUp__contentTag'>
              <Tag
                style='default'
                message={ content.tag }
              />
            </div>
          }

          {
            content.eyebrow &&
            <div className='OneUp__contentEyebrow'>
              <Text
                htmlTag='p'
                textStyle='eyebrow'
              >
                { content.eyebrow }
              </Text>
            </div>
          }
          { content.title &&
          <div className='OneUp__contentTitle'>
            <Text
              htmlTag='h5'
              textStyle='title-5'
            >
              { content.title }
            </Text>
          </div>
          }
          { content.titleSupport &&
          <div className='OneUp__contentTitlesupport'>
            <Text
              htmlTag='p'
              textStyle='body-1'
            >
              { content.titleSupport }
            </Text>
          </div>
          }
          {
            ( content.subtitle && content.subtitleSupport ) &&
            <>
              <div className='OneUp__contentSubTitle'>
                <Text
                  htmlTag='h6'
                  textStyle='title-6'
                >
                  { content.subtitle }
                </Text>
              </div>
              <div className='OneUp__contentSubTitlesupport'>
                <Text
                  htmlTag='p'
                  textStyle='body-2'
                >
                  { content.subtitleSupport }
                </Text>
              </div>
            </>
          }

          {
            ( content.subtitle2 && content.subtitleSupport2 ) &&
            <>
              <div className='OneUp__contentSubTitle'>
                <Text
                  htmlTag='h6'
                  textStyle='title-6'
                >
                  { content.subtitle2 }
                </Text>
              </div>
              <div className='OneUp__contentSubTitlesupport'>
                <Text
                  htmlTag='p'
                  textStyle='body-2'
                >
                  { content.subtitleSupport2 }
                </Text>
              </div>
            </>
          }
          {
            ( ( content.buttonAction && content.buttonAction.label ) || ( content.linkAction && content.linkAction.label ) ) &&
              <div className='OneUp__actions'>
                {
                  content.buttonAction && content.buttonAction.label &&
                  <div className='OneUp__contentButton' >
                    <Button
                      label={ content.buttonAction.label }
                      target='_self'
                      url={ content.buttonAction.url }
                      withHover
                    />
                  </div>
                }
                {
                  content.linkAction && content.linkAction.label &&
                  <div className='OneUp__contentLabel'>
                    <Link_Huge
                      target='_self'
                      action={ content.linkAction }
                      withHover
                    >
                      { content.linkAction.label }
                    </Link_Huge>
                  </div>
                }
              </div>
          }
        </div>
      </div>
    </div>
  );
};

/**
  * Property type definitions
  * @typedef OneUpProps
  * @type {object}
  * @property {object} title - Sets the title properties
  * @property {string} subtitle - Sets the subtitle
  * @property {string} imageDisplayMode - Sets the imageDisplayMode
  * @property {string} backgroundColor - Sets the backgroundColor
  * @property {object} article - Sets the article properties
  */

export const propTypes =  {
  title: PropTypes.object,
  subtitle: PropTypes.string,
  backgroundColor: PropTypes.string,
  article: PropTypes.object
};

OneUp.propTypes = propTypes;


export default OneUp;
